import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOMeta from '../components/SEOMeta/SEOMeta';
import Breadcrumbs from '../components/breadcrumbs/Breadcrumbs';
import { Navbar } from '../components';
import './RoadmapCoursePage.css';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'roadmap', label: 'Step-by-Step Roadmap' },
  { id: 'career', label: 'Career & Placement' },
  { id: 'faq', label: 'FAQs' },
];

const getStepLabel = (stepTitle, fallbackIndex) => {
  if (!stepTitle) return `Step ${fallbackIndex + 1}`;
  if (stepTitle.includes(':')) return stepTitle.split(':')[0].trim();
  const trimmed = stepTitle.trim();
  return trimmed.length > 24 ? `${trimmed.slice(0, 24)}...` : trimmed;
};

const RoadmapCoursePage = ({ config }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const chartRows = useMemo(() => (config.placementStats || []), [config.placementStats]);
  const steps = config.roadmapSteps || [];
  const safeActiveStepIndex = Math.min(activeStepIndex, Math.max(steps.length - 1, 0));
  const activeStep = steps[safeActiveStepIndex];
  const progressWidth = steps.length > 1
    ? `${(safeActiveStepIndex / (steps.length - 1)) * 100}%`
    : '0%';
  const breadcrumbItems = [
    { label: 'Home', to: '/' },
    { label: 'Courses', to: '/courses' },
    { label: 'Roadmaps', to: '/courses-roadmaps' },
    { label: config.badge },
  ];

  return (
    <main className="roadmap-page">
      <SEOMeta
        title={config.seoTitle}
        description={config.seoDescription}
        url={config.path}
        type="WebPage"
        noindex={config.noindex || false}
      />

      <div className="gradient__bg">
        <Navbar />
      </div>

      <Breadcrumbs items={breadcrumbItems} />

      <section className="roadmap-shell">
        <div className="roadmap-hero">
          <article className="roadmap-hero-main">
            <div className="roadmap-badge">{config.badge}</div>
            <h1>
              {config.headingPrefix} <span>{config.headingHighlight}</span>
            </h1>
            <p>{config.heroText}</p>
            {config.roadmapNotice && <p className="roadmap-note">{config.roadmapNotice}</p>}

            <div className="roadmap-cta-row">
              <a className="roadmap-cta-primary" href={config.syllabusLink} target="_blank" rel="noreferrer">
                Download Syllabus
              </a>
              <button
                type="button"
                className="roadmap-cta-ghost"
                onClick={() => navigate(config.enrollPath)}
              >
                Explore Our Courses
              </button>
              <button
                type="button"
                className="roadmap-cta-ghost"
                onClick={() => navigate('/?fromEnroll=true#enroll-form')}
              >
                Talk to Advisor
              </button>
            </div>

            <div className="roadmap-meta-grid">
              {config.quickStats.map((stat) => (
                <article key={stat.label}>
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </article>
              ))}
            </div>
          </article>

          <aside className="roadmap-hero-side">
            <h3 style={{ marginTop: 0 }}>Tech You Will Use</h3>
            <div className="roadmap-tech-cluster">
              {config.techStack.map((tech) => (
                <div className="roadmap-tech-item" key={tech.name}>
                  <img src={tech.icon} alt={tech.name} loading="lazy" />
                  <div>
                    <strong>{tech.name}</strong>
                    <div style={{ color: 'var(--roadmap-muted-text)', fontSize: '0.86rem' }}>{tech.note}</div>
                  </div>
                </div>
              ))}
            </div>

            {config.prerequisites?.length > 0 && (
              <div className="roadmap-prereq-wrap">
                <h4>Beginner Prerequisites</h4>
                <ul>
                  {config.prerequisites.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="student-strip" aria-label="Student stories">
              {config.studentImages.map((src, i) => (
                <img src={src} alt={`Student ${i + 1}`} key={src} loading="lazy" />
              ))}
            </div>
          </aside>
        </div>

        <div className="roadmap-tabs" role="tablist" aria-label="Roadmap tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`roadmap-tab${activeTab === tab.id ? ' active' : ''}`}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'roadmap') setActiveStepIndex(0);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <section className="roadmap-panel">
          {activeTab === 'overview' && (
            <div className="outcome-grid">
              {config.overviewCards.map((card) => (
                <article key={card.title} className="outcome-card">
                  <h4>{card.title}</h4>
                  <p style={{ margin: 0, color: 'var(--roadmap-muted-text)', lineHeight: 1.55 }}>{card.text}</p>
                </article>
              ))}
            </div>
          )}

          {activeTab === 'roadmap' && (
            <div>
              <div className="roadmap-mobile-steps" aria-label="Roadmap mobile step selector">
                <div className="roadmap-mobile-steps-grid">
                  {steps.map((step, idx) => {
                    const isActive = idx === safeActiveStepIndex;
                    return (
                      <button
                        key={`mobile-${step.title}`}
                        type="button"
                        className={`roadmap-mobile-step-btn${isActive ? ' active' : ''}`}
                        onClick={() => setActiveStepIndex(idx)}
                      >
                        <span>{step.icon || '📘'}</span>
                        <span>{step.shortLabel || getStepLabel(step.title, idx)}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="roadmap-mobile-step-meta">
                  Step {safeActiveStepIndex + 1} of {steps.length}
                </div>
              </div>

              <div className="roadmap-visual-stepper" aria-label="Roadmap progression">
                <div className="stepper-track" aria-hidden="true">
                  <span className="stepper-track-progress" style={{ width: progressWidth }} />
                </div>

                <div className="stepper-points-wrap">
                  {steps.map((step, idx) => {
                    const isActive = idx === safeActiveStepIndex;
                    return (
                      <button
                        key={step.title}
                        type="button"
                        className={`stepper-point${isActive ? ' active' : ''}`}
                        onClick={() => setActiveStepIndex(idx)}
                        title={step.title}
                        aria-label={`Open ${step.title}`}
                      >
                        <span className="stepper-point-icon">{step.icon || '📘'}</span>
                        <span className="stepper-point-dot" />
                        <span className="stepper-point-label">{step.shortLabel || getStepLabel(step.title, idx)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {activeStep && (
                <div className="stepper-grid">
                <article key={activeStep.title} className="stepper-card">
                  <span className="step-dot">{safeActiveStepIndex + 1}</span>
                  <div>
                    <div className="stepper-title-row">
                      <h3>{activeStep.title}</h3>
                      <button
                        type="button"
                        className="step-tooltip-btn"
                        data-tip={activeStep.tooltip || 'Practice with one mini-task before moving to the next milestone.'}
                        aria-label="Step guidance"
                      >
                        i
                      </button>
                    </div>
                    <p>{activeStep.summary}</p>
                    <ul>
                      {activeStep.bullets.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </article>
                </div>
              )}
            </div>
          )}

          {activeTab === 'career' && (
            <div className="outcome-grid">
              <article className="outcome-card">
                <h4>Placement Chance Tracker</h4>
                {chartRows.map((row) => (
                  <div key={row.label} style={{ marginBottom: '0.62rem' }}>
                    <div className="bar-label">
                      <span>{row.label}</span>
                      <span>{row.value}%</span>
                    </div>
                    <div className="bar-track">
                      <div className="bar-fill" style={{ width: `${row.value}%` }} />
                    </div>
                  </div>
                ))}
              </article>

              <article className="outcome-card">
                <h4>Interview Focus Areas</h4>
                <ul style={{ margin: 0, paddingLeft: '1rem', color: 'var(--roadmap-muted-text)' }}>
                  {config.interviewFocus.map((point) => (
                    <li key={point} style={{ marginBottom: '0.45rem' }}>{point}</li>
                  ))}
                </ul>
                <h4 style={{ marginTop: '1rem' }}>Job Opportunities</h4>
                <ul style={{ margin: 0, paddingLeft: '1rem', color: 'var(--roadmap-muted-text)' }}>
                  {config.jobRoles.map((role) => (
                    <li key={role} style={{ marginBottom: '0.4rem' }}>{role}</li>
                  ))}
                </ul>
              </article>
            </div>
          )}

          {activeTab === 'faq' && (
            <div className="faq-list">
              {config.faqs.map((faq) => (
                <article className="faq-item" key={faq.q}>
                  <h4>{faq.q}</h4>
                  <p>{faq.a}</p>
                </article>
              ))}
            </div>
          )}
        </section>
      </section>
    </main>
  );
};

export default RoadmapCoursePage;
